import { useEffect, useRef, useState } from "react";

export interface RootState {
  ispmMfe: {
    isChatOverlayVisible: boolean;
    isSuggestionVisible: boolean;
    userQuery: string;
  };
  user: {
    domainInfo: {
      ISPM_SERVICES_ENABLED: boolean;
      PAM_JIT_FOR_APPS_ENABLED: boolean;
    };
    userInfo: {
      id: number;
      roles: string[];
    };
    isMenuPinned: boolean;
    headerMenu: { ecmDateFormat: string };
  };
}

interface Store {
  dispatch: unknown;
  getState: () => RootState;
  subscribe: (listener: () => void) => () => void;
}

declare global {
  interface Window {
    store?: Store;
  }
}

/** Fallback state when host store is not yet available */
function getFallbackState(): RootState {
  const isMenuPinned =
    typeof window !== "undefined" &&
    localStorage.getItem("gen2.layout.isMenuPinned") === "true";
  return {
    ispmMfe: {
      isChatOverlayVisible: false,
      isSuggestionVisible: false,
      userQuery: "",
    },
    user: {
      domainInfo: {
        ISPM_SERVICES_ENABLED: false,
        PAM_JIT_FOR_APPS_ENABLED: false,
      },
      userInfo: { id: 0, roles: [] },
      isMenuPinned,
      headerMenu: { ecmDateFormat: "MMM dd, yyyy" },
    },
  };
}

type SelectorFn<T> = (state: RootState) => T;

function useRootSelector<T>(selectorFn: SelectorFn<T>): T {
  const selectorRef = useRef(selectorFn);
  selectorRef.current = selectorFn;

  const [selectedState, setSelectedState] = useState<T>(() => {
    try {
      const state =
        typeof window !== "undefined" && window.store
          ? (window.store.getState() as RootState)
          : getFallbackState();
      return selectorRef.current(state);
    } catch {
      return selectorRef.current(getFallbackState());
    }
  });

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let pollId: ReturnType<typeof setInterval> | undefined;

    const trySubscribe = (): boolean => {
      if (typeof window === "undefined" || !window.store) return false;
      try {
        const store = window.store;
        const state = store.getState() as RootState;
        setSelectedState(selectorRef.current(state));
        unsubscribe = store.subscribe(() => {
          setSelectedState(selectorRef.current(store.getState() as RootState));
        });
        return true;
      } catch {
        return false;
      }
    };

    if (!trySubscribe()) {
      pollId = setInterval(() => {
        if (trySubscribe() && pollId !== undefined) clearInterval(pollId);
      }, 50);
    }

    return () => {
      if (pollId !== undefined) clearInterval(pollId);
      unsubscribe?.();
    };
  }, []);

  return selectedState;
}

export default useRootSelector;
